import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { X, Save, Trash2, ExternalLink, Calculator, Link2, Unlink } from "lucide-react";
import { useHomeOSState } from "../state/HomeOSStateProvider";
import { getMealSlots, MEAL_STATUS_OPTIONS, calculateMealNutrition, buildMealSlotFromRecipe } from "../utils/mealPlanUtils";
import { RecipeSearchPicker } from "./RecipeSearchPicker";

const INPUT_CLS = "w-full rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm font-medium text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-rose-300";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[11px] font-extrabold uppercase tracking-wider text-gray-500 dark:text-gray-400">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

export function MealSlotEditModal({ open, day, slotId, onClose }) {
  const {
    weeklyMealPlan, recipes, members, mealPlanSettings,
    updateMealSlot, clearMealSlot,
  } = useHomeOSState();

  const slotCfg = useMemo(
    () => getMealSlots(mealPlanSettings).find(s => s.id === slotId),
    [mealPlanSettings, slotId]
  );

  const dayObj = useMemo(
    () => (weeklyMealPlan || []).find(d => d.day === day),
    [weeklyMealPlan, day]
  );
  const initial = useMemo(() => {
    const slot = dayObj?.slots?.[slotId];
    return {
      title: slot?.title || "",
      recipeId: slot?.recipeId || "",
      imageUrl: slot?.imageUrl || "",
      assignedCook: slot?.assignedCook || "",
      status: slot?.status || "Planned",
      servings: slot?.servings ?? 1,
      caloriesPerServe: slot?.caloriesPerServe ?? "",
      protein: slot?.macros?.protein ?? "",
      carbs: slot?.macros?.carbs ?? "",
      fat: slot?.macros?.fat ?? "",
      notes: slot?.notes || "",
    };
  }, [dayObj, slotId]);

  const [draft, setDraft] = useState(initial);
  const [pickerOpen, setPickerOpen] = useState(false);
  useEffect(() => { if (open) { setDraft(initial); setPickerOpen(false); } }, [open, initial]);

  if (!open || !slotCfg) return null;

  const linkedRecipe = (recipes || []).find(r => r.id === draft.recipeId) || null;

  /* Pull/refresh the meal slot fields from the currently linked recipe. */
  const pullFromRecipe = () => {
    if (!linkedRecipe) {
      toast("Link a recipe first to pull its details.");
      return;
    }
    const built = buildMealSlotFromRecipe(linkedRecipe, { keepCook: true, existingCook: draft.assignedCook || null });
    setDraft(d => ({
      ...d,
      title: built.title,
      imageUrl: built.imageUrl || d.imageUrl,
      servings: built.servings,
      caloriesPerServe: built.caloriesPerServe ?? d.caloriesPerServe,
      protein: built.macros.protein ?? d.protein,
      carbs:   built.macros.carbs   ?? d.carbs,
      fat:     built.macros.fat     ?? d.fat,
      assignedCook: d.assignedCook || built.assignedCook || "",
    }));
    toast.success("Pulled latest details from recipe.");
  };

  const selectRecipe = (recipe) => {
    if (!recipe) return;
    const built = buildMealSlotFromRecipe(recipe, { keepCook: true, existingCook: draft.assignedCook || null });
    setDraft(d => ({
      ...d,
      recipeId: recipe.id,
      title: d.title && d.recipeId !== recipe.id ? d.title : built.title,
      imageUrl: d.imageUrl || built.imageUrl,
      servings: built.servings,
      caloriesPerServe: built.caloriesPerServe ?? d.caloriesPerServe,
      protein: built.macros.protein ?? d.protein,
      carbs:   built.macros.carbs   ?? d.carbs,
      fat:     built.macros.fat     ?? d.fat,
      assignedCook: d.assignedCook || built.assignedCook || "",
    }));
    setPickerOpen(false);
  };

  const unlinkRecipe = () => {
    setDraft(d => ({ ...d, recipeId: "" }));
    toast("Recipe unlinked from this slot.");
  };

  const calcNutrition = () => {
    const n = calculateMealNutrition({
      caloriesPerServe: draft.caloriesPerServe || null,
      macros: { protein: draft.protein || null, carbs: draft.carbs || null, fat: draft.fat || null },
    }, linkedRecipe);
    if (n.calories === null && n.protein === null) {
      toast("Nutrition not calculated yet — add values manually below.");
      return;
    }
    setDraft(d => ({
      ...d,
      caloriesPerServe: n.calories ?? d.caloriesPerServe,
      protein: n.protein ?? d.protein,
      carbs: n.carbs ?? d.carbs,
      fat: n.fat ?? d.fat,
    }));
    toast.success("Nutrition pulled from recipe.");
  };

  const save = () => {
    updateMealSlot(day, slotId, {
      title: draft.title.trim(),
      recipeId: draft.recipeId || null,
      imageUrl: draft.imageUrl,
      assignedCook: draft.assignedCook || null,
      status: draft.status,
      servings: Number(draft.servings) || 1,
      caloriesPerServe: draft.caloriesPerServe === "" ? null : Number(draft.caloriesPerServe),
      macros: {
        protein: draft.protein === "" ? null : Number(draft.protein),
        carbs:   draft.carbs === ""   ? null : Number(draft.carbs),
        fat:     draft.fat === ""     ? null : Number(draft.fat),
      },
      notes: draft.notes,
    });
    toast.success(`${slotCfg.label} updated.`);
    onClose?.();
  };

  const clear = () => {
    clearMealSlot(day, slotId);
    toast(`${slotCfg.label} cleared.`);
    onClose?.();
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center" data-testid="meal-slot-edit-modal">
      <div className="absolute inset-0 bg-black/55 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-xl max-h-[92vh] overflow-y-auto bg-white dark:bg-slate-900 rounded-t-[28px] sm:rounded-[28px] shadow-none font-home">
        <div className={`sticky top-0 z-10 px-6 pt-5 pb-3 border-b border-gray-100 dark:border-slate-800 ${slotCfg.tile}`}>
          <div className="flex items-center justify-between gap-2">
            <div>
              <p className={`text-[10px] font-extrabold uppercase tracking-[0.18em] ${slotCfg.text}`}>{day} · {slotCfg.label}</p>
              <h2 className="text-xl font-extrabold text-gray-900 dark:text-gray-50">Edit meal slot</h2>
            </div>
            <button onClick={onClose} className="w-8 h-8 rounded-xl bg-white/85 text-gray-600 flex items-center justify-center" data-testid="meal-slot-edit-close">
              <X className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>

        <div className="px-6 py-5 space-y-3">
          <Field label="Meal title">
            <input value={draft.title} onChange={e => setDraft(d => ({ ...d, title: e.target.value }))} className={INPUT_CLS} placeholder="e.g. Butter chicken with rice" data-testid="meal-slot-input-title" />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <span className="text-[11px] font-extrabold uppercase tracking-wider text-gray-500 dark:text-gray-400">Linked recipe</span>
              <div className="mt-1">
                {linkedRecipe ? (
                  <LinkedRecipePreview
                    recipe={linkedRecipe}
                    onChange={() => setPickerOpen(true)}
                    onUnlink={unlinkRecipe}
                    onPull={pullFromRecipe}
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => setPickerOpen(true)}
                    className={`${INPUT_CLS} flex items-center justify-between gap-2 hover:border-[color:var(--ara-line-soft)]`}
                    data-testid="meal-slot-pick-recipe-btn"
                  >
                    <span className="text-gray-500 truncate">Choose from Family Recipe Book…</span>
                    <Link2 className="w-4 h-4 text-[color:var(--ara-ink-muted)] flex-shrink-0" strokeWidth={1.5} />
                  </button>
                )}
              </div>
            </div>
            <Field label="Assigned cook">
              <select value={draft.assignedCook} onChange={e => setDraft(d => ({ ...d, assignedCook: e.target.value }))} className={INPUT_CLS} data-testid="meal-slot-input-cook">
                <option value="">Unassigned</option>
                <option value="all">Everyone</option>
                {(members || []).map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </Field>
          </div>

          {pickerOpen && (
            <div className="rounded-2xl border border-[color:var(--ara-line-soft)] dark:border-rose-800 bg-transparent dark:bg-transparent p-3" data-testid="meal-slot-recipe-picker">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[11px] font-extrabold uppercase tracking-wider text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)]">Pick a recipe for {slotCfg.label}</p>
                <button
                  type="button"
                  onClick={() => setPickerOpen(false)}
                  className="text-[11px] font-extrabold text-gray-500 hover:text-gray-800 dark:hover:text-gray-200"
                  data-testid="meal-slot-recipe-picker-close"
                >
                  Done
                </button>
              </div>
              <RecipeSearchPicker
                recipes={recipes || []}
                slotId={slotId}
                selectedRecipeId={draft.recipeId || null}
                onSelect={selectRecipe}
                testIdPrefix="meal-slot-recipe-search"
              />
            </div>
          )}

          <Field label="Photo URL (optional)">
            <input value={draft.imageUrl} onChange={e => setDraft(d => ({ ...d, imageUrl: e.target.value }))} className={INPUT_CLS} placeholder="https://…" data-testid="meal-slot-input-image" />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Servings">
              <input type="number" min="0" step="0.5" value={draft.servings} onChange={e => setDraft(d => ({ ...d, servings: e.target.value }))} className={INPUT_CLS} data-testid="meal-slot-input-servings" />
            </Field>
            <Field label="Status">
              <select value={draft.status} onChange={e => setDraft(d => ({ ...d, status: e.target.value }))} className={INPUT_CLS} data-testid="meal-slot-input-status">
                {MEAL_STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
          </div>

          <div className="rounded-2xl bg-gray-50 dark:bg-slate-800/60 border border-gray-200 dark:border-slate-700 p-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[11px] font-extrabold uppercase tracking-wider text-gray-500 dark:text-gray-400">Nutrition (per serve)</p>
              <button onClick={calcNutrition} className="inline-flex items-center gap-1 text-[10px] font-extrabold text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)] hover:underline" data-testid="meal-slot-pull-recipe-nutrition">
                <Calculator className="w-3 h-3" strokeWidth={1.5} /> Pull from recipe
              </button>
            </div>
            {linkedRecipe && linkedRecipe.caloriesPerServe == null && !draft.caloriesPerServe && (
              <p className="text-[11px] font-bold text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)] italic mb-1" data-testid="meal-slot-nutrition-missing">
                Nutrition not calculated yet — add values manually below.
              </p>
            )}
            <div className="grid grid-cols-4 gap-2">
              <Field label="Calories">
                <input type="number" min="0" value={draft.caloriesPerServe} onChange={e => setDraft(d => ({ ...d, caloriesPerServe: e.target.value }))} className={INPUT_CLS} data-testid="meal-slot-input-cal" />
              </Field>
              <Field label="Protein g">
                <input type="number" min="0" value={draft.protein} onChange={e => setDraft(d => ({ ...d, protein: e.target.value }))} className={INPUT_CLS} data-testid="meal-slot-input-protein" />
              </Field>
              <Field label="Carbs g">
                <input type="number" min="0" value={draft.carbs} onChange={e => setDraft(d => ({ ...d, carbs: e.target.value }))} className={INPUT_CLS} data-testid="meal-slot-input-carbs" />
              </Field>
              <Field label="Fat g">
                <input type="number" min="0" value={draft.fat} onChange={e => setDraft(d => ({ ...d, fat: e.target.value }))} className={INPUT_CLS} data-testid="meal-slot-input-fat" />
              </Field>
            </div>
          </div>

          <Field label="Notes">
            <textarea value={draft.notes} onChange={e => setDraft(d => ({ ...d, notes: e.target.value }))} rows={2} className={INPUT_CLS} data-testid="meal-slot-input-notes" />
          </Field>
        </div>

        <div className="sticky bottom-0 bg-white/95 dark:bg-slate-900/95 backdrop-blur px-6 py-3 border-t border-gray-100 dark:border-slate-800 flex items-center gap-2">
          <button onClick={clear} className="inline-flex items-center gap-1 px-3 py-2 rounded-full bg-transparent hover:bg-transparent dark:bg-transparent text-[color:var(--ara-ink-muted)] border border-[color:var(--ara-line-soft)] dark:border-rose-700 text-xs font-extrabold" data-testid="meal-slot-clear">
            <Trash2 className="w-3 h-3" strokeWidth={1.5} /> Clear
          </button>
          {linkedRecipe && (
            <button
              onClick={() => { window.location.href = `/home-os/meals`; }}
              className="inline-flex items-center gap-1 px-3 py-2 rounded-full bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-xs font-extrabold text-gray-700 dark:text-gray-200"
              data-testid="meal-slot-view-recipe"
            >
              <ExternalLink className="w-3 h-3" strokeWidth={1.5} /> View recipe
            </button>
          )}
          <button onClick={onClose} className="ml-auto px-3 py-2 rounded-full text-sm font-bold text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-800" data-testid="meal-slot-cancel">Cancel</button>
          <button onClick={save} className="inline-flex items-center gap-1.5 bg-transparent hover:opacity-80 text-white font-extrabold text-sm px-4 py-2 rounded-full shadow-md" data-testid="meal-slot-save">
            <Save className="w-4 h-4" strokeWidth={1.5} /> Save
          </button>
        </div>
      </div>
    </div>
  );
}

export default MealSlotEditModal;

/* ── Linked recipe preview (shown when a recipe is attached) ──────── */

function LinkedRecipePreview({ recipe, onChange, onUnlink, onPull }) {
  const totalMin = (recipe.prepTime || 0) + (recipe.cookTime || 0);
  return (
    <div className="rounded-xl border border-[color:var(--ara-line-soft)] dark:border-emerald-800 bg-transparent dark:bg-transparent p-2.5" data-testid="meal-slot-linked-recipe">
      <div className="flex items-center gap-2.5">
        <div className="w-12 h-12 rounded-lg overflow-hidden flex items-center justify-center     dark: dark: dark: flex-shrink-0">
          {recipe.imageUrl ? (
            <img src={recipe.imageUrl} alt={recipe.title} className="w-full h-full object-cover" />
          ) : (
            <span className="text-2xl" aria-hidden>{recipe.imageEmoji || "🍽️"}</span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-extrabold text-gray-900 dark:text-gray-50 truncate" data-testid="meal-slot-linked-recipe-title">{recipe.title}</p>
          <div className="flex items-center gap-1.5 mt-0.5 text-[10px] font-bold text-gray-600 dark:text-gray-300">
            <span className="px-1.5 py-0.5 rounded-full bg-transparent dark:bg-transparent text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)] font-extrabold uppercase tracking-wider">{recipe.category || "Recipe"}</span>
            {recipe.serves ? <span>Serves {recipe.serves}</span> : null}
            {totalMin > 0 && <span>· {totalMin}m</span>}
            {recipe.caloriesPerServe ? <span>· {Math.round(recipe.caloriesPerServe)} kcal</span> : null}
          </div>
        </div>
      </div>
      <div className="flex flex-wrap gap-1 mt-2">
        <button type="button" onClick={onChange} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-[11px] font-extrabold text-gray-700 dark:text-gray-200" data-testid="meal-slot-linked-recipe-change">
          <Link2 className="w-3 h-3" strokeWidth={1.5} /> Change recipe
        </button>
        <button type="button" onClick={onPull} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-transparent hover:opacity-80 text-white text-[11px] font-extrabold" data-testid="meal-slot-linked-recipe-pull">
          <Calculator className="w-3 h-3" strokeWidth={1.5} /> Pull from recipe
        </button>
        <a href="/home-os/meals/recipe-book" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-[11px] font-extrabold text-gray-700 dark:text-gray-200" data-testid="meal-slot-linked-recipe-view">
          <ExternalLink className="w-3 h-3" strokeWidth={1.5} /> View recipe
        </a>
        <button type="button" onClick={onUnlink} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-transparent hover:bg-transparent dark:bg-transparent text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)] border border-[color:var(--ara-line-soft)] dark:border-rose-700 text-[11px] font-extrabold" data-testid="meal-slot-linked-recipe-unlink">
          <Unlink className="w-3 h-3" strokeWidth={1.5} /> Unlink
        </button>
      </div>
    </div>
  );
}
